import { CASHSALE_UNDERPAYMENT_LIMIT } from "@saleor/config";
import {
  GetArOutstandingQueryResult,
  OrderViewDataQuery
} from "@saleor/graphql";
import { roundDecimal } from "@saleor/utils/number";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import * as Yup from "yup";

import {
  DocumentType,
  FeeType,
  ICashsaleFormValues,
  IDocumentFile,
  PaymentMethod
} from "./types";

export const MAX_TOTAL_FILES_SIZE = {
  value: 10 * 1024 * 1024,
  text: "10 MB"
};

export const ACCEPTED_FILE_EXTENSION: string[] = [
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".ppt",
  ".pptx",
  ".pdf",
  ".txt",
  ".csv",
  ".jpg",
  ".jpeg",
  ".png",
  ".gif"
];

export interface UseCashSaleFormProps {
  isEditable?: boolean;
  orderAmount: number;
  arOutstandingResult?: GetArOutstandingQueryResult;
  cashSaleData?: OrderViewDataQuery["orderViewData"]["cashSale"];
}

const safeNumber = (value: any) => {
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

const numberField = () =>
  Yup.number().transform((v, o) => {
    if (["", "-", null, undefined].includes(o)) {
      return 0;
    }
    return safeNumber(v);
  });

const useCashSaleForm = ({
  isEditable = false,
  orderAmount,
  arOutstandingResult,
  cashSaleData
}: UseCashSaleFormProps) => {
  const intl = useIntl();

  const isSkipArOutstanding =
    !arOutstandingResult?.data && !arOutstandingResult?.error;
  const customerBalanceAmount = safeNumber(
    isSkipArOutstanding
      ? cashSaleData?.arAmount
      : arOutstandingResult?.data?.getArOutstanding?.currentAmount
  );

  const arOutstanding = arOutstandingResult?.data?.getArOutstanding;
  const isFailedArOutstanding =
    (arOutstanding && !arOutstanding.status) || arOutstandingResult?.error;

  const [isFormMounted, setIsFormMounted] = useState<boolean>(false);
  const hasOrderAmountChangedWhileUnmounted = useRef<boolean>(false);
  const previousOrderAmount = useRef<number>(orderAmount);

  const formikInitialValues: ICashsaleFormValues = {
    orderAmount: "",
    arAmount: 0,
    cashFlag: !!cashSaleData?.cashFlag,
    cashAmount: roundDecimal(cashSaleData?.cashAmount) || "",
    balanceFlag: !!cashSaleData?.balanceFlag,
    balanceAmount: roundDecimal(cashSaleData?.balanceAmount) || "",
    customerPaymentDifference: 0,
    fee: roundDecimal(cashSaleData?.feeAmount) || "",
    feeType: (cashSaleData?.feeType as FeeType) ?? FeeType.BAHT,
    feeAbsorbFlag: !!cashSaleData?.feeAbsorbFlag,
    withHoldTax: roundDecimal(cashSaleData?.withHoldTax) || "",
    remark: cashSaleData?.remark ?? "",
    documentFiles: (cashSaleData?.files ?? []).map(item => ({
      id: item.id,
      file: {
        name: item.fileName,
        size: item.fileSize
      } as IDocumentFile["file"],
      documentType: item.documentType as DocumentType,
      paymentMethod: item.paymentMethod as PaymentMethod
    }))
  };

  const formikValidationSchema = Yup.object().shape({
    cashAmount: numberField().when("cashFlag", {
      is: true,
      then: schema => schema.required("").positive(""),
      otherwise: schema => schema.notRequired()
    }),
    balanceAmount: numberField().when("balanceFlag", {
      is: true,
      then: schema =>
        schema
          .required("")
          .positive("")
          .test("max-balanceAmount", function(value) {
            if (isFailedArOutstanding) {
              return true;
            }

            const { orderAmount = 0, cashAmount = 0 } = this.parent;
            const maxValue = roundDecimal(
              safeNumber(orderAmount) - safeNumber(cashAmount),
              2
            );

            if (value > maxValue) {
              return this.createError({
                message: "ไม่สามารถใช้เงินเหลือเกินยอดที่ต้องชำระได้"
              });
            }
            return true;
          }),
      otherwise: schema => schema.notRequired()
    }),
    customerPaymentDifference: numberField().min(
      CASHSALE_UNDERPAYMENT_LIMIT,
      "ลูกค้าจ่ายเงินขาด เกินกำหนด"
    ),
    documentFiles: Yup.array().of(
      Yup.mixed().test("file-item-validation", function(value?: IDocumentFile) {
        if (!value?.file) {
          return this.createError({ message: "กรุณาอัปโหลดไฟล์" });
        }
        const ext =
          "." +
          value.file.name
            .split(".")
            .pop()
            ?.toLowerCase();
        if (!ext || !ACCEPTED_FILE_EXTENSION.includes(ext)) {
          return this.createError({
            message: "ประเภทไฟล์ไม่ถูกต้อง"
          });
        }
        return true;
      })
    )
  });

  const formik = useFormik<ICashsaleFormValues>({
    enableReinitialize: true,
    initialValues: formikInitialValues,
    validateOnMount: true,
    validationSchema: isEditable ? formikValidationSchema : null,
    validate: values => {
      if (!isEditable) {
        return null;
      }
      const errors: any = {};

      const documentErrors = validateDocumentFileSummary(values);
      if (documentErrors.length > 0) {
        errors.documentFilesSummary = documentErrors;
      }

      return errors;
    },
    onSubmit: null
  });

  const validateDocumentFileSummary = (
    values: ICashsaleFormValues
  ): string[] => {
    const documentErrors: string[] = [];

    const files = values.documentFiles;
    const totalFileSize = files.reduce(
      (sum, f) => sum + (f.file?.size || 0),
      0
    );

    // Check: Pay-in Slip required when cash payment
    const needsPayinSlip = !!values.cashFlag;
    const hasPayinSlip = files.some(f => f.documentType === DocumentType.PAYIN);

    if (needsPayinSlip && !hasPayinSlip) {
      documentErrors.push("กรุณาอัปโหลดไฟล์ Pay-in Slip/Statement");
    }

    // Check: Total file size limit
    if (totalFileSize > MAX_TOTAL_FILES_SIZE.value) {
      documentErrors.push(
        `ขนาดไฟล์รวมทั้งหมดเกิน <b>${MAX_TOTAL_FILES_SIZE.text}</b>, กรุณาลบไฟล์บางส่วน`
      );
    }

    return documentErrors;
  };

  const feeTypeOptions = [
    {
      label: intl.formatMessage({ defaultMessage: "THB" }),
      value: "BAHT"
    },
    {
      label: intl.formatMessage({ defaultMessage: "%" }),
      value: "PERCENT"
    }
  ];

  const handleFormMountChange = (mounted: boolean) => {
    setIsFormMounted(mounted);

    if (!mounted) {
      hasOrderAmountChangedWhileUnmounted.current = false;
    }
  };

  useEffect(() => {
    formik.setFieldValue("orderAmount", orderAmount);
    formik.setFieldValue("arAmount", customerBalanceAmount);
  }, [orderAmount, customerBalanceAmount]);

  const updateCashAmountIfNeeded = () => {
    const { cashFlag, cashAmount } = formik?.values ?? {};
    if (
      !!cashFlag &&
      safeNumber(cashAmount) === 0 &&
      safeNumber(orderAmount) > 0
    ) {
      formik.setFieldValue("cashAmount", orderAmount);
    }
  };

  useEffect(() => {
    if (orderAmount === previousOrderAmount.current) {
      return;
    }
    if (isFormMounted) {
      updateCashAmountIfNeeded();
    } else {
      hasOrderAmountChangedWhileUnmounted.current = true;
    }
    previousOrderAmount.current = orderAmount;
  }, [orderAmount, isFormMounted]);

  useEffect(() => {
    if (isFormMounted && hasOrderAmountChangedWhileUnmounted.current) {
      updateCashAmountIfNeeded();
      hasOrderAmountChangedWhileUnmounted.current = false;
    }
  }, [isFormMounted]);

  const preventCashAmountAutoFillOnNextMount = () => {
    hasOrderAmountChangedWhileUnmounted.current = false;
  };

  // Calculate CustomerPaymentDifference
  useEffect(() => {
    const {
      orderAmount = 0,
      cashFlag,
      balanceFlag,
      cashAmount = 0,
      balanceAmount = 0,
      withHoldTax = 0
    } = formik?.values ?? {};
    const enabled = cashFlag || balanceFlag;
    const diff = enabled
      ? (safeNumber(orderAmount) -
          safeNumber(withHoldTax) -
          (safeNumber(cashAmount) + safeNumber(balanceAmount))) *
        -1
      : 0;
    formik.setFieldValue("customerPaymentDifference", roundDecimal(diff, 2));
  }, [
    formik?.values?.orderAmount,
    formik?.values?.cashFlag,
    formik?.values?.balanceFlag,
    formik?.values?.cashAmount,
    formik?.values?.balanceAmount,
    formik?.values?.withHoldTax
  ]);

  // Validate form when change to 'Edit' mode
  useEffect(() => {
    if (isEditable) {
      formik.validateForm();
    }
  }, [isEditable]);

  const handleChangeField = (e: React.ChangeEvent<any>) => {
    const name = e.target?.name as keyof ICashsaleFormValues;
    const checked = e.target?.checked;

    if (name === "cashFlag") {
      formik.setFieldValue(
        "cashAmount",
        (checked ? formik.values.orderAmount : "") || ""
      );
    } else if (name === "balanceFlag") {
      formik.setFieldValue("balanceAmount", "");
    }

    formik.handleChange(e);
  };

  const handleBlurField = (e: React.FocusEvent<any>) => {
    const name = e.target?.name as keyof ICashsaleFormValues;
    const value = e.target?.value;
    const inputMode = e.target?.inputMode;

    if (inputMode === "numeric" && parseFloat(value) === 0) {
      // wait for NumberFormat to finish setting value - prevent re-render loop
      setTimeout(() => formik.setFieldValue(name, ""));
    }
  };

  const resetForm = () => {
    formik.setValues({
      ...formik.initialValues,
      orderAmount: formik.values.orderAmount,
      arAmount: formik.values.arAmount
    });
  };

  return {
    formik,
    arOutstandingResult,
    isEditable,
    feeTypeOptions,
    handleFormMountChange,
    handleChangeField,
    handleBlurField,
    resetForm,
    preventCashAmountAutoFillOnNextMount
  };
};

export default useCashSaleForm;
